import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { NoiseOverlay } from "@/components/luxury";
import FooterQuickLinks from "@/components/FooterQuickLinks";
import OrganizationSchema from "@/components/OrganizationSchema";
import { PawPrintIcon as PawPrint, ShieldCheckIcon as ShieldCheck, DnaIcon as Dna, TreeStructureIcon as TreeStructure, LockKeyIcon as LockKey, IdentificationBadgeIcon as IdentificationBadge, ArrowRightIcon as ArrowRight, GraduationCapIcon as GraduationCap, CalendarCheckIcon as CalendarCheck, DogIcon as Dog, HouseLineIcon as HouseLine, ScissorsIcon as Scissors, BuildingsIcon as Buildings } from "@phosphor-icons/react";

/** Public "About Us" page — luxury editorial revamp (June 2026).
 *
 *  Brand-first and persona-inclusive: cinematic dark hero, the six-persona
 *  ecosystem, a trust bento (DNA / COI / escrow / verified people), the
 *  Armstrong family story, and a worldwide-aware closing CTA that reads
 *  the live signup policy. Self-contained shell (no AppShell) so it works
 *  for unauthenticated visitors.
 */

const HERO_IMG =
  "https://images.pexels.com/photos/4695939/pexels-photo-4695939.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PERSONAS = [
  {
    icon: Dog,
    label: "Dog owners",
    body: "A permanent home for your dog's records, photos, health milestones, and story — whether or not you ever breed.",
    to: "/registration",
    cta: "Register your dog",
  },
  {
    icon: HouseLine,
    label: "Puppy buyers",
    body: "Verified listings, pedigree depth, escrow-protected payments, and a bill of rights written for you.",
    to: "/marketplace",
    cta: "Browse puppies",
  },
  {
    icon: PawPrint,
    label: "Breeders",
    body: "Six-generation pedigrees, litter tools, heat-cycle tracking, storefronts, and buyers who can trust what they see.",
    to: "/kennels",
    cta: "Explore kennels",
  },
  {
    icon: GraduationCap,
    label: "Trainers",
    body: "Sell live video sessions and courses, issue completion certificates, and get paid through the platform.",
    to: "/courses",
    cta: "See training",
  },
  {
    icon: Scissors,
    label: "Service providers",
    body: "Groomers, sitters, vets, and USDA-licensed transporters — background-checked, reviewed, and bookable.",
    to: "/find-pros",
    cta: "Find pros",
  },
  {
    icon: CalendarCheck,
    label: "Event organizers",
    body: "Shows, meetups, and competitions with Stripe-backed registration and transparent receipts.",
    to: "/events",
    cta: "View events",
  },
];

const TRUST = [
  {
    icon: Dna,
    title: "DNA verification",
    body: "DNA-verified registration tiers so a pedigree is more than a promise.",
    span: "md:col-span-3",
  },
  {
    icon: TreeStructure,
    title: "Inbreeding warnings",
    body: "Coefficient-of-inbreeding checks fire the moment a sire and dam are linked — before a litter exists.",
    span: "md:col-span-3",
  },
  {
    icon: LockKey,
    title: "Escrow protection",
    body: "Money is held until both sides confirm. Disputes are mediated by a human, not a form letter.",
    span: "md:col-span-2",
  },
  {
    icon: IdentificationBadge,
    title: "Verified people",
    body: "Background-checked providers, verified breeders, and identity checks where money changes hands.",
    span: "md:col-span-2",
  },
  {
    icon: ShieldCheck,
    title: "Records that outlive us",
    body: "Auditable, portable, transferable in a 60-second flow — not a 90-day saga.",
    span: "md:col-span-2",
  },
];

// Admin-managed photo slots (kept from the previous page) with curated
// fallbacks so frames never flash empty.
const FALLBACK_PHOTOS = {
  founder: {
    url: "https://images.unsplash.com/photo-1701602117585-08f12689d03c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjB3aXRoJTIwZG9nJTIwc3Vuc2V0fGVufDB8fHx8MTc4MTMwMTAzMnww&ixlib=rb-4.1.0&q=85",
    alt: "A couple sitting with their dog at sunset",
  },
  mission: {
    url: "https://images.unsplash.com/photo-1620289052446-202137ffa876?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBkb2clMjB0cmFpbmluZ3xlbnwwfHx8fDE3ODEzMDEwMzJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Handler working with a German Shepherd",
  },
};

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

export default function About() {
  const [photos, setPhotos] = useState(FALLBACK_PHOTOS);
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    api.get("/about/photos")
      .then((r) => { if (r.data) setPhotos((p) => ({ ...p, ...r.data })); })
      .catch(() => {});
    api.get("/public/signup-policy")
      .then((r) => setPolicy(r.data))
      .catch(() => {});
  }, []);

  const worldwide = policy && policy.us_only === false;

  return (
    <div className="min-h-screen bg-[#FAF7F0] font-manrope text-ink">
      <OrganizationSchema />
      <Helmet>
        <title>About · Armstrong K9 Registry — The standard of trust for every dog</title>
        <meta
          name="description"
          content="Founded by the Armstrong family: a trust-and-safety registry for dog owners, puppy buyers, breeders, trainers, and verified pros. DNA verification, inbreeding warnings, escrow protection."
        />
        <link rel="canonical" href="https://armstrongk9registry.com/about" />
        <meta property="og:title" content="About Armstrong K9 Registry — The standard of trust for every dog" />
        <meta
          property="og:description"
          content="Not just for breeders. Owners, buyers, trainers, and verified pros meet around the same verified records."
        />
        <meta property="og:url" content="https://armstrongk9registry.com/about" />
        <meta property="og:image" content="https://armstrongk9registry.com/og-card.png" />
        <meta name="twitter:image" content="https://armstrongk9registry.com/og-card.png" />
      </Helmet>
      {/* Top brand strip */}
      <header className="bg-[#0E0E0E] border-b border-[#C9A227]/20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="about-brand-link">
            <img src="/logo-256.png" alt="" className="h-8 w-8 rounded-full object-contain" />
            <span className="font-spectral text-base tracking-tight text-white">
              Armstrong K9 Registry
            </span>
          </Link>
          <nav className="hidden sm:flex items-center gap-5 text-sm text-white/70">
            <Link to="/media" className="hover:text-white" data-testid="about-nav-media">
              Media
            </Link>
            <Link to="/login" className="hover:text-white" data-testid="about-nav-login">
              Sign in
            </Link>
            <Link
              to="/register"
              className="rounded-full bg-gold-gradient text-ink px-4 py-1.5 text-xs font-bold tracking-wide hover:opacity-95 transition-opacity"
              data-testid="about-nav-register"
            >
              Join the registry
            </Link>
          </nav>
        </div>
      </header>

      {/* ============ Hero — cinematic dark ============ */}
      <section className="relative bg-[#0E0E0E] text-white overflow-hidden" data-testid="about-hero">
        <NoiseOverlay opacity={0.06} />
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-y-0 right-0 w-full md:w-3/5 h-full object-cover opacity-35"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E0E0E] via-[#0E0E0E]/85 to-[#0E0E0E]/30" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <p
            className="text-xs uppercase tracking-[0.25em] text-gold font-bold animate-in fade-in slide-in-from-bottom-2 duration-700"
            data-testid="about-hero-tagline"
          >
            Pedigree &middot; Passion &middot; Purpose
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 max-w-3xl leading-[1.05] animate-in fade-in slide-in-from-bottom-3 duration-700">
            The standard of trust
            <br />
            for every dog.
          </h1>
          <div className="gold-hairline mt-8 mb-6 max-w-xs opacity-60" />
          <p className="text-base md:text-lg text-white/70 max-w-2xl leading-relaxed animate-in fade-in duration-1000">
            Not just for breeders. Armstrong K9 Registry is where owners,
            buyers, breeders, trainers, service professionals, and event
            organizers meet around the same verified records — purebreds,
            rare bloodlines, and exotic breeds alike.
          </p>
        </div>
      </section>

      {/* ============ Ecosystem — six personas ============ */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="about-ecosystem">
        <p className="text-xs uppercase tracking-[0.25em] text-[#9E7A15] font-bold">One registry, six doors in</p>
        <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight mt-3 max-w-2xl">
          Whatever a dog means to you, you belong here.
        </h2>
        <div className="mt-12 md:mt-16 grid md:grid-cols-2 lg:grid-cols-3">
          {PERSONAS.map(({ icon: Icon, label, body, to, cta }, i) => (
            <Link
              key={label}
              to={to}
              data-testid={`about-persona-${label.toLowerCase().replace(/ /g, "-")}`}
              className={`group border-t border-[#C9A227]/30 py-8 md:py-10 lg:pr-10 ${i % 3 !== 0 ? "lg:pl-10 lg:border-l" : ""} ${i % 2 !== 0 ? "md:pl-8 md:border-l lg:border-l-0 lg:pl-10" : ""} ${i % 3 !== 0 ? "lg:border-l-[#C9A227]/30" : ""} hover:bg-white/60 transition-colors`}
            >
              <Icon size={26} weight="duotone" className="text-[#9E7A15]" />
              <h3 className="font-spectral text-xl md:text-2xl tracking-tight mt-4">{label}</h3>
              <p className="text-sm text-[#3F3F3F] leading-relaxed mt-2">{body}</p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-[12px] font-bold uppercase tracking-[0.15em] text-ink group-hover:gap-2.5 transition-all">
                {cta} <ArrowRight size={12} weight="bold" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ Trust bento — dark ============ */}
      <section className="relative bg-[#0E0E0E] text-white py-24 md:py-32 overflow-hidden" data-testid="about-trust">
        <NoiseOverlay opacity={0.05} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold">Why people trust the seal</p>
          <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight mt-3 max-w-2xl">
            Trust isn&rsquo;t a tagline. It&rsquo;s infrastructure.
          </h2>
          <div className="mt-12 md:mt-16 grid md:grid-cols-6 gap-4">
            {TRUST.map(({ icon: Icon, title, body, span }) => (
              <div
                key={title}
                className={`${span} border border-[#C9A227]/25 rounded-2xl p-7 md:p-8 bg-white/[0.02]`}
                data-testid={`about-trust-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <Icon size={24} weight="duotone" className="text-gold" />
                <h3 className="font-spectral text-xl tracking-tight mt-4">{title}</h3>
                <p className="text-sm text-white/60 leading-relaxed mt-2">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Family story — Swiss split ============ */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="about-founder-card">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#9E7A15] font-bold">Founded by the Armstrong family</p>
            <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight mt-3">
              Better records, healthier dogs, stronger communities.
            </h2>
            <div className="gold-hairline mt-7 mb-7 max-w-xs" />
            <p className="text-base text-[#3F3F3F] leading-relaxed">
              The Armstrong family has spent decades around working and
              companion dogs — raising them, training them, and learning from
              every line. This registry is the tool we always wished we had:
              rigorous enough for serious pedigree work, and friendly enough
              that a first-time owner can keep the same quality of records
              for the dog asleep at their feet.
            </p>
            <p className="text-base text-[#3F3F3F] leading-relaxed mt-4">
              Records about a dog should outlive whoever first wrote them.
              They should warn you when two dogs are too closely related,
              travel with the dog to its next home, and never be held
              hostage. We built everything else — the marketplace, the
              training, the events, the pros — around that simple promise.
            </p>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-[#C9A227]/30">
              <img
                src={resolveUrl(photos.founder?.url)}
                alt={photos.founder?.alt || ""}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
            {/* Wax-seal motif */}
            <img
              src="/logo-256.png"
              alt=""
              className="absolute -bottom-6 -left-6 h-20 w-20 rounded-full border-4 border-[#FAF7F0] shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* ============ Worldwide CTA — dark ============ */}
      <section className="relative bg-[#0E0E0E] text-white py-24 md:py-28 overflow-hidden" data-testid="about-worldwide-cta">
        <NoiseOverlay opacity={0.05} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 text-left">
          <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold inline-flex items-center gap-2">
            {worldwide ? (
              <>
                <span className="h-2 w-2 rounded-full bg-[#C1272D] animate-pulse" />
                Now live worldwide
              </>
            ) : (
              "Serving the United States"
            )}
          </p>
          <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight mt-3 max-w-2xl">
            {worldwide
              ? "Wherever you and your dog are, the Registry is open."
              : "Worldwide launch is coming. The records start now."}
          </h2>
          <p className="text-sm md:text-base text-white/60 mt-4 max-w-xl leading-relaxed">
            {worldwide
              ? "Register dogs, build pedigrees, and connect with breeders from anywhere. Pricing in USD."
              : "Join today and your dog's permanent record is ready the day we open the doors to the world."}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              className="rounded-full bg-gold-gradient text-ink px-7 py-3 text-sm font-bold hover:opacity-95 transition-opacity inline-flex items-center gap-2"
              data-testid="about-register-btn"
            >
              Join the registry <ArrowRight size={14} weight="bold" />
            </Link>
            <Link
              to="/contact"
              className="rounded-full border border-[#C9A227]/50 text-gold px-7 py-3 text-sm font-bold hover:bg-white/5 transition-colors"
              data-testid="about-contact-btn"
            >
              Get in touch
            </Link>
          </div>
        </div>
      </section>

      {/* Business classification — discoverable by D&B verification
          scrapers + Google's Knowledge Graph. The codes here MUST
          match whatever you put on the D-U-N-S application
          (https://www.dnb.com). Source of truth: OrganizationSchema.jsx. */}
      <section
        data-testid="about-business-classification"
        className="bg-[#FAFAFA] border-t border-[#E5E5E5]"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
          <div className="flex items-center gap-3 mb-4">
            <Buildings size={20} weight="duotone" className="text-gold" />
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#8A6A00]">
              Business Classification
            </p>
          </div>
          <h2 className="font-spectral text-3xl text-ink mb-4">
            Verifiable, registered, accountable.
          </h2>
          <p className="text-sm text-[#5C5C5C] max-w-2xl leading-relaxed mb-6">
            For verification agencies, lenders, and partners: Armstrong K9
            Registry LLC is a California limited liability company operating
            under the trade name Armstrong K9 Registry. Standard industry
            codes below match our D-U-N-S filing.
          </p>
          <dl className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl text-sm">
            <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
              <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B]">
                Legal entity
              </dt>
              <dd className="text-ink font-semibold mt-1">
                Armstrong K9 Registry LLC
              </dd>
              <dd className="text-[11px] text-[#8B8B8B] mt-1">
                California, USA · Founded May 22, 2026
              </dd>
            </div>
            <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
              <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B]">
                SIC code
              </dt>
              <dd className="text-ink font-mono font-semibold mt-1">0752</dd>
              <dd className="text-[11px] text-[#8B8B8B] mt-1">
                Animal Specialty Services (except Veterinary)
              </dd>
            </div>
            <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
              <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B]">
                NAICS code
              </dt>
              <dd className="text-ink font-mono font-semibold mt-1">812910</dd>
              <dd className="text-[11px] text-[#8B8B8B] mt-1">
                Pet Care (except Veterinary) Services
              </dd>
            </div>
            <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
              <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B]">
                ISIC v4 code
              </dt>
              <dd className="text-ink font-mono font-semibold mt-1">9609</dd>
              <dd className="text-[11px] text-[#8B8B8B] mt-1">
                Other personal service activities n.e.c.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E5E5] bg-white">
        <FooterQuickLinks tone="light" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#6B6B6B]">
          <p>© {new Date().getFullYear()} Armstrong K9 Registry</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link to="/about" className="hover:text-ink" data-testid="about-footer-about">About</Link>
            <Link to="/terms" className="hover:text-ink">Terms</Link>
            <Link to="/privacy" className="hover:text-ink">Privacy</Link>
            <Link to="/ethics" className="hover:text-ink">Ethics</Link>
            <Link to="/contact" className="hover:text-ink">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
